import { z } from 'zod';
export const AUTOMATION_TRIGGERS = {
    'ticket.created': { label: 'Ticket erstellt', entity: 'ticket' },
    'email.received': { label: 'E-Mail eingegangen (neue Anfrage oder Antwort)', entity: 'ticket' },
    'whatsapp.received': { label: 'WhatsApp-Nachricht eingegangen (neue Anfrage oder Antwort)', entity: 'ticket' },
    'ticket.status_changed': { label: 'Ticketstatus geändert', entity: 'ticket' },
    'ticket.idle': {
        label: 'Ticket unbearbeitet',
        entity: 'ticket',
        scheduled: true,
        params: [{ key: 'hours', label: 'Stunden ohne Aktivität', default: 48, min: 1, max: 2160 }],
    },
    'ticket.sla_response_breached': { label: 'SLA: Reaktionszeit überschritten', entity: 'ticket' },
    'ticket.sla_resolution_warning': {
        label: 'SLA: Lösungszeit läuft bald ab',
        entity: 'ticket',
        scheduled: true,
        params: [{ key: 'minutesBefore', label: 'Minuten vor Ablauf', default: 60, min: 5, max: 10080 }],
    },
    'ticket.sla_resolution_breached': { label: 'SLA: Lösungszeit überschritten', entity: 'ticket' },
    'repair.created': { label: 'Gerät angenommen (Reparatur angelegt)', entity: 'repair' },
    'repair.status_changed': { label: 'Reparaturstatus geändert', entity: 'repair' },
    'repair.quote_approved': { label: 'Kostenvoranschlag freigegeben', entity: 'repair' },
    'repair.quote_rejected': { label: 'Kostenvoranschlag abgelehnt', entity: 'repair' },
    'monitoring.problem': { label: 'Monitoring-Problem gemeldet (Zabbix/NetDoc)', entity: 'monitoring_event' },
    'contract.expiring': {
        label: 'Wartungsvertrag läuft bald ab',
        entity: 'contract',
        scheduled: true,
        params: [{ key: 'days', label: 'Tage vor Vertragsende', default: 30, min: 1, max: 365 }],
    },
    'contract.activated': { label: 'Vertrag aktiv (Wartungsvertrag oder AVV)', entity: 'contract' },
    'contract.expired': { label: 'Wartungsvertrag abgelaufen', entity: 'contract' },
    'appointment.created': { label: 'Termin erstellt', entity: 'appointment' },
};
export const TRIGGER_KEYS = Object.keys(AUTOMATION_TRIGGERS);
const PRIORITY_OPTIONS = [
    { value: 'LOW', label: 'Niedrig' },
    { value: 'NORMAL', label: 'Normal' },
    { value: 'HIGH', label: 'Hoch' },
    { value: 'URGENT', label: 'Dringend' },
];
const CATEGORY_OPTIONS = [
    { value: 'NEW', label: 'Neu' },
    { value: 'OPEN', label: 'Offen' },
    { value: 'WAITING', label: 'Wartend' },
    { value: 'RESOLVED', label: 'Gelöst' },
    { value: 'CLOSED', label: 'Geschlossen' },
];
export const REPAIR_STATUS_OPTIONS = [
    { value: 'RECEIVED', label: 'Angenommen' },
    { value: 'DIAGNOSIS', label: 'In Diagnose' },
    { value: 'QUOTE_PENDING', label: 'Kostenvoranschlag offen' },
    { value: 'APPROVED', label: 'Freigegeben' },
    { value: 'IN_REPAIR', label: 'In Reparatur' },
    { value: 'WAITING_PARTS', label: 'Wartet auf Teile' },
    { value: 'TESTING', label: 'Im Test' },
    { value: 'READY_FOR_PICKUP', label: 'Abholbereit' },
    { value: 'PICKED_UP', label: 'Abgeholt' },
    { value: 'CANCELLED', label: 'Storniert' },
];
export const AUTOMATION_FIELDS = {
    ticket: {
        priority: { label: 'Priorität', type: 'enum', options: PRIORITY_OPTIONS },
        statusCategory: { label: 'Statuskategorie', type: 'enum', options: CATEGORY_OPTIONS },
        previousStatusCategory: { label: 'Vorherige Statuskategorie', type: 'enum', options: CATEGORY_OPTIONS },
        areaId: { label: 'Bereich', type: 'area' },
        source: {
            label: 'Quelle',
            type: 'enum',
            options: [
                { value: 'MANUAL', label: 'Manuell' },
                { value: 'PORTAL', label: 'Kundenportal' },
                { value: 'FORM', label: 'Formular' },
                { value: 'EMAIL', label: 'E-Mail' },
                { value: 'MONITORING', label: 'Monitoring' },
                { value: 'WHATSAPP', label: 'WhatsApp' },
            ],
        },
        hasCustomer: { label: 'Hat Kunde', type: 'boolean' },
        hasContract: { label: 'Kunde hat aktiven Vertrag', type: 'boolean' },
        assigned: { label: 'Ist zugewiesen', type: 'boolean' },
        customerId: { label: 'Kunde', type: 'customer' },
        subject: { label: 'Betreff', type: 'text' },
        description: { label: 'Text der Anfrage', type: 'text' },
        fromAddress: { label: 'Absender (E-Mail-Adresse)', type: 'text' },
        fromDomain: { label: 'Absender-Domain', type: 'text' },
        mailbox: { label: 'Postfach (E-Mail eingegangen)', type: 'text' },
        mailText: { label: 'Text der E-Mail (E-Mail eingegangen)', type: 'text' },
        hasAttachments: { label: 'E-Mail hat Anhänge', type: 'boolean' },
        isReply: { label: 'Antwort auf bestehendes Ticket', type: 'boolean' },
    },
    repair: {
        status: { label: 'Status', type: 'enum', options: REPAIR_STATUS_OPTIONS },
        previousStatus: { label: 'Vorheriger Status', type: 'enum', options: REPAIR_STATUS_OPTIONS },
        hasContract: { label: 'Kunde hat aktiven Vertrag', type: 'boolean' },
    },
    monitoring_event: {
        severity: {
            label: 'Schweregrad',
            type: 'enum',
            options: ['Nicht klassifiziert', 'Information', 'Warnung', 'Durchschnitt', 'Hoch', 'Katastrophe'].map((label, i) => ({ value: String(i), label })),
        },
        hasTicket: { label: 'Ticket vorhanden', type: 'boolean' },
        hasCustomer: { label: 'Host hat Kunde', type: 'boolean' },
        hasContract: { label: 'Kunde hat aktiven Vertrag', type: 'boolean' },
    },
    contract: {
        kind: { label: 'Art', type: 'enum', options: [{ value: 'SERVICE', label: 'Wartungsvertrag' }, { value: 'DPA', label: 'Auftragsverarbeitung (AVV)' }] },
        type: { label: 'Vertragsart', type: 'text' },
        autoRenew: { label: 'Automatische Verlängerung', type: 'boolean' },
    },
    appointment: {
        hasCustomer: { label: 'Kunde eingeladen', type: 'boolean' },
        online: { label: 'Online-Besprechung', type: 'boolean' },
    },
};
export const CONDITION_OPS = {
    eq: 'ist',
    neq: 'ist nicht',
    in: 'ist einer von',
    gte: 'mindestens',
    lte: 'höchstens',
    contains: 'enthält',
    not_contains: 'enthält nicht',
    starts_with: 'beginnt mit',
    ends_with: 'endet mit',
};
export const automationConditionSchema = z.object({
    field: z.string().min(1).max(50),
    op: z.enum(['eq', 'neq', 'in', 'gte', 'lte', 'contains', 'not_contains', 'starts_with', 'ends_with']),
    value: z.union([z.string().max(200), z.number(), z.boolean(), z.array(z.string().max(100)).max(20)]),
});
const recipient = z
    .string()
    .max(254)
    .regex(/^(customer|assignee|team|owner|technicians|role:[a-z0-9_-]+|email:[^\s@]+@[^\s@]+\.[^\s@]+)$/, 'Ungültiger Empfänger');
export const actionSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('send_email'),
        recipients: z.array(recipient).min(1).max(20),
        subject: z.string().trim().min(1).max(200),
        body: z.string().trim().min(1).max(5000),
    }),
    z.object({ type: z.literal('set_priority'), priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']) }),
    z.object({ type: z.literal('set_ticket_status'), statusId: z.string().uuid() }),
    z.object({ type: z.literal('assign'), userId: z.string().uuid().nullish(), teamId: z.string().uuid().nullish() }),
    z.object({ type: z.literal('add_note'), text: z.string().trim().min(1).max(5000) }),
    z.object({
        type: z.literal('create_ticket'),
        areaId: z.string().uuid(),
        priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']),
        subject: z.string().trim().max(200).optional(),
    }),
    z.object({ type: z.literal('set_repair_status'), status: z.enum(['IN_REPAIR', 'WAITING_PARTS', 'TESTING', 'DIAGNOSIS']) }),
    z.object({ type: z.literal('revoke_area_grants') }),
    z.object({ type: z.literal('release_billing'), note: z.string().trim().max(1000).nullish() }),
    z.object({
        type: z.literal('invite_portal'),
        /** auto: Geschäftskunde → Kundenadministrator (erster Zugang) bzw. Mitarbeiter, Privatkunde → Privatkunde */
        role: z.enum(['auto', 'customer_admin', 'customer_employee', 'private_customer']).default('auto'),
    }),
    z.object({
        type: z.literal('print_document'),
        document: z.enum(['intake', 'workshop', 'label', 'quote', 'report', 'pickup', 'contract']),
        /** leer = Standarddrucker des Belegs */
        printerId: z.string().uuid().nullish(),
        copies: z.number().int().min(1).max(20).nullish(),
    }),
    z.object({ type: z.literal('sync_outlook') }),
    z.object({ type: z.literal('set_customer'), customerId: z.string().uuid(), contactId: z.string().uuid().nullish() }),
    z.object({ type: z.literal('set_area'), areaId: z.string().uuid(), categoryId: z.string().uuid().nullish() }),
    z.object({
        type: z.literal('print_ticket'),
        /** ticket = Ticket als PDF, attachments = PDF-Anhänge (bei „E-Mail eingegangen“ die der Mail, sonst alle des Tickets) */
        what: z.enum(['ticket', 'attachments', 'both']).default('ticket'),
        printerId: z.string().uuid().nullish(),
        copies: z.number().int().min(1).max(20).nullish(),
    }),
    z.object({
        type: z.literal('notify_teams'),
        channelId: z.string().uuid(),
        title: z.string().trim().min(1).max(200),
        text: z.string().trim().max(4000).default(''),
    }),
    z.object({
        type: z.literal('notify_whatsapp'),
        /** Mitarbeitende (Handynummer aus dem Benutzerprofil) oder feste Nummern (phone:+49…) */
        recipients: z.array(z.string().max(60).regex(/^(assignee|team|technicians|owner|role:[a-z0-9_-]+|phone:\+?[0-9 ]{8,20})$/, 'Ungültiger Empfänger')).min(1).max(20),
        text: z.string().trim().min(1).max(1000),
    }),
]);
export const ACTION_DEFS = {
    send_email: { label: 'E-Mail senden', entities: ['ticket', 'repair', 'monitoring_event', 'contract', 'appointment'] },
    set_priority: { label: 'Ticketpriorität setzen', entities: ['ticket', 'monitoring_event'] },
    set_ticket_status: { label: 'Ticketstatus setzen', entities: ['ticket'] },
    assign: { label: 'Zuweisen (Bearbeiter/Team)', entities: ['ticket', 'monitoring_event'] },
    add_note: { label: 'Interne Notiz ins Ticket', entities: ['ticket', 'monitoring_event'] },
    create_ticket: { label: 'Ticket erstellen', entities: ['monitoring_event', 'contract'] },
    set_repair_status: { label: 'Reparaturstatus setzen', entities: ['repair'] },
    revoke_area_grants: { label: 'Manuelle Bereichsfreigaben des Vertrags entziehen', entities: ['contract'] },
    release_billing: { label: 'Zur Abrechnung freigeben (Bereit zur Rechnung)', entities: ['ticket', 'repair'] },
    print_document: { label: 'Beleg drucken', entities: ['repair', 'contract'] },
    invite_portal: { label: 'Kundenportal-Zugang einladen (falls noch keiner besteht)', entities: ['ticket', 'repair', 'contract'] },
    sync_outlook: { label: 'Outlook-Kalender aktualisieren', entities: ['appointment'] },
    set_customer: { label: 'Kunde zuordnen', entities: ['ticket'] },
    set_area: { label: 'Bereich/Kategorie setzen', entities: ['ticket'] },
    print_ticket: { label: 'Ticket bzw. Anhänge drucken', entities: ['ticket'] },
    notify_teams: { label: 'Teams-Nachricht (Kanal)', entities: ['ticket', 'repair', 'monitoring_event', 'contract', 'appointment'] },
    notify_whatsapp: { label: 'WhatsApp an Mitarbeitende', entities: ['ticket', 'repair', 'monitoring_event', 'contract', 'appointment'] },
};
export const RECIPIENT_LABELS = {
    customer: 'Kunde',
    assignee: 'Bearbeiter',
    team: 'Team-Mitglieder',
    owner: 'Vertragsverantwortlicher',
    technicians: 'Termin-Mitarbeitende',
};
const stepId = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/);
export const FLOW_LIMITS = { depth: 6, steps: 80, waitMinutesMax: 60 * 24 * 30 };
export const flowStepSchema = z.lazy(() => z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('action'), id: stepId, action: actionSchema }),
    z.object({
        kind: z.literal('branch'),
        id: stepId,
        label: z.string().trim().max(80).optional(),
        branches: z
            .array(z.object({
            id: stepId,
            label: z.string().trim().min(1).max(80),
            match: z.enum(['all', 'any']).default('all'),
            conditions: z.array(automationConditionSchema).min(1, 'Jeder Zweig braucht eine Bedingung').max(20),
            steps: z.array(flowStepSchema).max(40),
        }))
            .min(1)
            .max(10),
        otherwise: z.array(flowStepSchema).max(40).default([]),
    }),
    z.object({ kind: z.literal('wait'), id: stepId, minutes: z.number().int().min(1).max(FLOW_LIMITS.waitMinutesMax), label: z.string().trim().max(80).optional() }),
    z.object({ kind: z.literal('stop'), id: stepId, label: z.string().trim().max(80).optional() }),
]));
/** Alle Schritte eines Ablaufs (rekursiv) mit Tiefe – für Prüfungen und Zählungen. */
export function walkFlow(steps, visit, depth = 1) {
    for (const s of steps) {
        visit(s, depth);
        if (s.kind === 'branch') {
            for (const b of s.branches)
                walkFlow(b.steps, visit, depth + 1);
            walkFlow(s.otherwise, visit, depth + 1);
        }
    }
}
export const automationRuleSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullish(),
    active: z.boolean().default(true),
    trigger: z.enum(TRIGGER_KEYS),
    triggerParams: z.record(z.string(), z.number()).default({}),
    conditions: z.array(automationConditionSchema).max(20).default([]),
    /** Einfache Regel: Aktionen der Reihe nach */
    actions: z.array(actionSchema).max(10).default([]),
    /** Ablauf mit Verzweigungen und Wartezeiten – ersetzt die einfachen Aktionen */
    flow: z.array(flowStepSchema).max(40).nullish(),
    sortOrder: z.number().int().min(0).max(10_000).default(100),
})
    .superRefine((rule, ctx) => {
    const def = AUTOMATION_TRIGGERS[rule.trigger];
    const fields = AUTOMATION_FIELDS[def.entity];
    rule.conditions.forEach((c, i) => {
        if (!fields[c.field])
            ctx.addIssue({ code: 'custom', path: ['conditions', i, 'field'], message: `Feld „${c.field}“ passt nicht zum Auslöser` });
    });
    if (rule.flow) {
        if (rule.flow.length === 0)
            ctx.addIssue({ code: 'custom', path: ['flow'], message: 'Der Ablauf braucht mindestens einen Schritt' });
        let count = 0;
        let actions = 0;
        const ids = new Set();
        walkFlow(rule.flow, (s, depth) => {
            count++;
            if (ids.has(s.id))
                ctx.addIssue({ code: 'custom', path: ['flow'], message: `Schritt-ID „${s.id}“ doppelt` });
            ids.add(s.id);
            if (depth > FLOW_LIMITS.depth)
                ctx.addIssue({ code: 'custom', path: ['flow'], message: `Höchstens ${FLOW_LIMITS.depth} Ebenen verschachteln` });
            if (s.kind === 'action') {
                actions++;
                if (!ACTION_DEFS[s.action.type].entities.includes(def.entity)) {
                    ctx.addIssue({ code: 'custom', path: ['flow'], message: `Aktion „${ACTION_DEFS[s.action.type].label}“ passt nicht zum Auslöser` });
                }
            }
            if (s.kind === 'branch') {
                for (const b of s.branches) {
                    for (const c of b.conditions) {
                        if (!fields[c.field])
                            ctx.addIssue({ code: 'custom', path: ['flow'], message: `Zweig „${b.label}“: Feld „${c.field}“ passt nicht zum Auslöser` });
                    }
                }
            }
        });
        if (count > FLOW_LIMITS.steps)
            ctx.addIssue({ code: 'custom', path: ['flow'], message: `Höchstens ${FLOW_LIMITS.steps} Schritte je Ablauf` });
        if (actions === 0)
            ctx.addIssue({ code: 'custom', path: ['flow'], message: 'Der Ablauf braucht mindestens eine Aktion' });
    }
    else if (rule.actions.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['actions'], message: 'Mindestens eine Aktion' });
    }
    rule.actions.forEach((a, i) => {
        if (!ACTION_DEFS[a.type].entities.includes(def.entity)) {
            ctx.addIssue({ code: 'custom', path: ['actions', i, 'type'], message: `Aktion „${ACTION_DEFS[a.type].label}“ passt nicht zum Auslöser` });
        }
    });
    for (const p of def.params ?? []) {
        const v = rule.triggerParams[p.key];
        if (v !== undefined && (v < p.min || v > p.max))
            ctx.addIssue({ code: 'custom', path: ['triggerParams', p.key], message: `${p.label}: ${p.min}–${p.max}` });
    }
});
/** Prüft eine Bedingung gegen einen Feldwert. */
export function matchCondition(actual, c) {
    const norm = (v) => (typeof v === 'boolean' || typeof v === 'number' ? v : v == null ? null : String(v));
    const a = norm(actual);
    switch (c.op) {
        case 'eq':
            return a === norm(c.value) || (typeof a === 'number' && a === Number(c.value)) || (typeof a === 'boolean' && String(a) === String(c.value));
        case 'neq':
            return !matchCondition(actual, { ...c, op: 'eq' });
        case 'in':
            return Array.isArray(c.value) && c.value.map(String).includes(String(a));
        case 'gte':
            return a !== null && Number(a) >= Number(c.value);
        case 'lte':
            return a !== null && Number(a) <= Number(c.value);
        case 'contains':
            return typeof a === 'string' && a.toLowerCase().includes(String(c.value).toLowerCase());
        case 'not_contains':
            return !(typeof a === 'string' && a.toLowerCase().includes(String(c.value).toLowerCase()));
        case 'starts_with':
            return typeof a === 'string' && a.toLowerCase().startsWith(String(c.value).toLowerCase());
        case 'ends_with':
            return typeof a === 'string' && a.toLowerCase().endsWith(String(c.value).toLowerCase());
    }
}
/** Ersetzt {{platzhalter}} in Texten. Unbekannte Platzhalter bleiben leer. */
export function renderTemplate(text, vars) {
    return text.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_, k) => {
        const v = vars[k];
        return v === null || v === undefined ? '' : String(v);
    });
}
export const TEMPLATE_VARIABLES = {
    ticket: ['ticket.number', 'ticket.subject', 'ticket.priority', 'ticket.status', 'ticket.area', 'customer.name', 'ticket.responseDue', 'ticket.resolutionDue'],
    repair: ['repair.number', 'repair.status', 'repair.device', 'customer.name'],
    monitoring_event: ['event.name', 'event.host', 'event.severity', 'customer.name', 'ticket.number'],
    contract: ['contract.number', 'contract.title', 'contract.type', 'contract.endDate', 'customer.name'],
    appointment: ['appointment.title', 'appointment.start', 'appointment.location', 'ticket.number', 'customer.name'],
};
//# sourceMappingURL=automation.js.map